<template>
  <v-row
    ><v-col>
      <v-label v-if="label" class="bold"
        >{{ label }}<span v-if="showColon">:</span>
        <span class="pl-1" v-if="labelValue">{{ labelValue }}</span></v-label
      >

      <v-text-field
        v-if="singleLine"
        :id="id"
        :aria-describedby="id"
        :aria-label="ariaLabel"
        v-model.lazy.trim="modelValue"
        :readonly="readonly"
        :counter="counter"
        :maxlength="maxlength"
        persistent-counter
        single-line
        :variant="readonly ? 'outlined' : 'filled'"
        @blur="trim"
      >
      </v-text-field>

      <v-textarea
        v-if="previousData"
        :id="id"
        :aria-describedby="id"
        :aria-label="ariaLabel"
        v-model.lazy.trim="previousNotesVal"
        :readonly="readonly"
        :counter="counter"
        :maxlength="maxlength"
        persistent-counter
        auto-grow
        rows="1"
        :variant="readonly ? 'outlined' : 'filled'"
      >
      </v-textarea>

      <v-textarea
        v-else
        :id="id"
        v-model.lazy.trim="modelValue"
        :aria-describedby="id"
        :aria-label="ariaLabel"
        :readonly="readonly"
        :counter="counter"
        :maxlength="maxlength"
        persistent-counter
        auto-grow
        rows="1"
        :variant="readonly ? 'outlined' : 'filled'"
        :required="isRequired"
        :rules="computedRules"
        @keypress="handler"
        @update:model-value="truncate"
      >
        <template #details>
          <div class="d-flex justify-space-between">
            <v-spacer />
            <span
              class="inputErrorMessage"
              v-if="secondCharacterCount && modelValueString?.length >= 1 && modelValueString?.length < 50 && !readonly"
              >{{ modelValueString?.length || 0 }} of 50 Characters Required
            </span>
            <span v-else-if="isRequired && !modelValue && !readonly" class="inputErrorMessage"> Required </span>
          </div>
        </template>
      </v-textarea>
    </v-col></v-row
  >
</template>
<script>
import { computed, ref } from "vue";
import { useInspectionStore } from "@/stores/inspection";
import { secondCharacterCountRule, requiredRule } from "@/composables/validationRules";
import { truncateString, encodeString } from "@/composables/util";

export default {
  name: "CommentTypeTextField",
  props: {
    id: {},
    inspectionCommentType: { default: "" },
    readonly: { type: Boolean, default: false },
    counter: { default: "" },
    maxlength: { maxlength: "" },
    singleLine: { type: Boolean, default: false },
    label: { default: "" },
    labelValue: { default: "" },
    showColon: { type: Boolean, default: true },
    secondCharacterCount: { type: Boolean, default: false },
    previousData: { type: Boolean, default: false },
    isRequired: { type: Boolean, default: false },
  },
  emits: ["changesDetected"],
  setup(props, ctx) {
    const inspectionStore = useInspectionStore();
    const modelValueString = ref("");
    const structure = computed(() => inspectionStore.selectedInspection);
    const previousNotesVal = computed(() => {
      return structure.value?.inspectionCommentsPrev?.find(
        (element) => element?.commentType == props.inspectionCommentType
      )?.notes;
    });
    const ariaLabel = computed({
      get() {
        if (props.label == "" || !props.label) {
          return props.id + " has no label";
        } else {
          return props.label + "_" + props.id;
        }
      },
    });
    function setElementByCommentType(object) {
      structure.value?.inspectionComments.map(function (element) {
        if (element?.commentType == object?.commentType) {
          element.notes = object.notes;
          element.updateUserId = object.updateUserId;
          element.updateDateTime = object.updateDateTime;
        }
      });
    }

    function getInspCommentObjectByCommentType(commentType) {
      return computed(() =>
        structure.value?.inspectionComments?.find((element) => element?.commentType == commentType)
      );
    }

    function getNotesByCommentType(commentType) {
      const object = getInspCommentObjectByCommentType(commentType);
      return object?.value?.notes;
    }

    function elseCase(commentType, notes) {
      return {
        brkey: structure.value.structureBridge.brkey,
        commentType: commentType,
        inspectionId: structure.value.inspection.inspectionId,
        updateDateTime: new Date().toISOString(),
        notes: notes,
        updateUserId: structure.value.inspection.updateUserId,
      };
    }

    const modelValue = computed({
      get() {
        return getNotesByCommentType(props.inspectionCommentType);
      },
      set(approachAlignmentParam) {
        modelValueString.value = encodeString(approachAlignmentParam);

        const approachAlignmentObject = getInspCommentObjectByCommentType(props.inspectionCommentType);

        if (approachAlignmentObject.value) {
          approachAlignmentObject.value.notes = modelValueString.value;
          approachAlignmentObject.value.updateUserId = structure.value.inspection.updateUserId;
          approachAlignmentObject.value.updateDateTime = new Date().toISOString();
          setElementByCommentType(approachAlignmentObject.value);
        } else {
          structure.value.inspectionComments.push(elseCase(props.inspectionCommentType, modelValueString.value));
        }
        ctx.emit("changesDetected");
      },
    });

    modelValueString.value = modelValue.value;

    const computedRules = computed(() => {
      let validationRules = [];

      if (props.rules && props.rules.length > 0) {
        validationRules = validationRules.concat(props.rules);
      }
      if (props.isRequired && !props.readonly) {
        validationRules.push(requiredRule);
      }
      if (props.secondCharacterCount && !props.readonly) {
        validationRules.push(secondCharacterCountRule(modelValueString.value));
      }
      return validationRules;
    });

    const handler = (event) => {
      if (event.which < 0x20 && event.which != 13) {
        // e.which < 0x20, then it's not a printable character like backspace etc.
        // e.which!=13 , enter key
        return; // Do nothing
      }

      if (modelValueString.value?.length >= Number.parseInt(props.maxlength)) {
        event.preventDefault();
        return false;
      }
    };

    const truncate = () => {
      modelValue.value = truncateString(
        modelValueString.value?.replaceAll(/\r\n|\r|\n/g, "\r\n"),
        Number.parseInt(props.maxlength)
      );
    };

    return {
      ariaLabel,
      modelValue,
      modelValueString,
      previousNotesVal,
      secondCharacterCountRule,
      computedRules,
      handler,
      truncate,
    };
  },
};
</script>
